import {
  Canvas,
  FileRecord,
  RESOURCE_TAG_FAVORITE,
  ResourceTagResourceType,
} from "@cohesive/api-client";
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { orgAPI } from "./api";

/**
 * Every React Query key family whose caches may embed a resource of each type.
 * A resource can be cached under more than its own list key — canvases, for
 * instance, are also embedded in workspace queries (`["workspaces", id,
 * "canvases"]`, the recent-by-workspace map) and the shared-with-me list
 * (`["canvas", ...]`). The toggle must patch + invalidate all of them, or list
 * items fetched from a different endpoint show a stale star. Mirrors the
 * canvas/skill mutation invalidation sets.
 */
const RELATED_QUERY_KEYS_BY_RESOURCE_TYPE: Record<
  ResourceTagResourceType,
  string[][]
> = {
  canvas: [["canvases"], ["canvas"], ["workspaces"], ["workspace"]],
  workspace: [["workspaces"], ["workspace"]],
  skill: [["skills"], ["skill"]],
  "file-record": [["fileRecords"], ["fileRecord"]],
  "integration-connection": [["integrationConnections"]],
};

interface ToggleFavoriteVariables {
  resourceType: ResourceTagResourceType;
  resourceId: string;
  /** Current state — when true we unfavorite, otherwise we favorite. */
  isFavorite: boolean;
}

/**
 * Toggle the "favorite" tag on a resource for the current user. Optimistically
 * patches the `tags` array on the resource across every cached list that can
 * hold it, then resyncs all of them on settle (which also updates favorites-list
 * membership). Mirrors the chat star toggle (useUpdateWorkspaceInboxItem).
 */
export function useToggleFavorite() {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, ToggleFavoriteVariables>({
    mutationFn: async ({ resourceType, resourceId, isFavorite }) => {
      if (isFavorite) {
        await orgAPI.resourceTags.remove({ resourceType, resourceId });
      } else {
        await orgAPI.resourceTags.create({
          resource_type: resourceType,
          resource_id: resourceId,
        });
      }
    },
    onMutate: ({ resourceType, resourceId, isFavorite }) => {
      // Patch the resource's `tags` in every array-shaped cache under each
      // related key family; map/object caches are skipped and resync onSettled.
      for (const queryKey of RELATED_QUERY_KEYS_BY_RESOURCE_TYPE[
        resourceType
      ]) {
        queryClient.setQueriesData({ queryKey }, (data: unknown) => {
          if (!Array.isArray(data)) return data;
          return data.map((item) => {
            if (
              !item ||
              typeof item !== "object" ||
              (item as { id?: string }).id !== resourceId
            ) {
              return item;
            }
            const tags = new Set<string>(
              (item as { tags?: string[] }).tags ?? [],
            );
            if (isFavorite) {
              tags.delete(RESOURCE_TAG_FAVORITE);
            } else {
              tags.add(RESOURCE_TAG_FAVORITE);
            }
            return { ...item, tags: Array.from(tags) };
          });
        });
      }
    },
    onSettled: (_data, _error, { resourceType }) => {
      for (const queryKey of RELATED_QUERY_KEYS_BY_RESOURCE_TYPE[
        resourceType
      ]) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}

export function useFavoriteCanvases(
  options?: Partial<UseQueryOptions<Canvas[], AxiosError>>,
) {
  return useQuery({
    queryKey: ["canvases", "favorite"],
    queryFn: () =>
      orgAPI.canvas.list(undefined, { params: { tag: RESOURCE_TAG_FAVORITE } }),
    ...options,
  });
}

export function useFavoriteFileRecords(
  options?: Partial<UseQueryOptions<FileRecord[], AxiosError>>,
) {
  return useQuery({
    queryKey: ["fileRecords", "favorite"],
    queryFn: () =>
      orgAPI.fileRecords.list(
        { class: "workspace" },
        { params: { tag: RESOURCE_TAG_FAVORITE } },
      ),
    ...options,
  });
}
